import { addCurrency } from "../utils/currencyUtils";

const isLineage = (name) => /^(linage|lineage)$/i.test(String(name || ""));
const isExpense = (name) => /^(expense|expenses|admin)$/i.test(String(name || ""));
const isPrizeFund = (name) => /^prize fund$/i.test(String(name || ""));

const otherFeeTotal = (fees = []) =>
  (fees || []).reduce((sum, fee) => addCurrency(sum, Number(fee.amount) || 0), 0);

const mergeOtherFees = (lists) => {
  const amounts = new Map();
  (lists || []).forEach((fees) => {
    (fees || []).forEach((fee) => {
      const name = fee?.name;
      if (!name) return;
      amounts.set(name, addCurrency(amounts.get(name) || 0, Number(fee.amount) || 0));
    });
  });
  return [...amounts.entries()]
    .map(([name, amount]) => ({ name, amount }))
    .filter((fee) => fee.amount > 0)
    .sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Buckets one tournament's get_event_entry_statistics rows.
 * Prize Fund is the RPC prize_fund (Prize Fund fees × entries, plus added money).
 * Linage and Expenses are the matching fee_totals. Prize Fund is not added twice.
 */
export const financeFromEventStats = (rows = []) => {
  let lineage = 0;
  let prizeFund = 0;
  let prizeFundFees = 0;
  let added = 0;
  let expenses = 0;
  let sawFee = false;
  let lineageLabel = "Linage";
  const other = new Map();

  (rows || []).forEach((row) => {
    const totals = row?.fee_totals || {};
    const names = Object.keys(totals);
    if (
      names.length ||
      Number(row?.prize_fund) > 0 ||
      Number(row?.additional_prize_fund) > 0
    ) {
      sawFee = true;
    }
    prizeFund = addCurrency(prizeFund, Number(row?.prize_fund) || 0);
    added = addCurrency(added, Number(row?.additional_prize_fund) || 0);
    names.forEach((name) => {
      const amount = Number(totals[name]) || 0;
      if (isLineage(name)) {
        lineage = addCurrency(lineage, amount);
      } else if (isExpense(name)) {
        expenses = addCurrency(expenses, amount);
      } else if (isPrizeFund(name)) {
        prizeFundFees = addCurrency(prizeFundFees, amount);
      } else if (amount > 0) {
        other.set(name, addCurrency(other.get(name) || 0, amount));
      }
    });
  });

  const otherFees = [...other.entries()]
    .map(([name, amount]) => ({ name, amount }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return {
    lineage,
    prizeFund,
    prizeFundFees,
    added,
    expenses,
    otherFees,
    total: addCurrency(lineage, prizeFund, expenses, otherFeeTotal(otherFees)),
    lineageLabel,
    usable: sawFee,
  };
};

export const yearFinance = (items = []) => {
  const included = (items || []).filter(
    (item) =>
      item?.usable &&
      (Number(item.lineage) > 0 ||
        Number(item.prizeFund) > 0 ||
        Number(item.expenses) > 0 ||
        otherFeeTotal(item.otherFees) > 0)
  );
  if (!included.length) return null;

  let lineage = 0;
  let prizeFund = 0;
  let prizeFundFees = 0;
  let added = 0;
  let expenses = 0;
  let lockedPayouts = null;
  let lineageLabel = "Linage";
  const sources = new Set();

  const tournaments = included.map((item) => {
    lineage = addCurrency(lineage, item.lineage);
    prizeFund = addCurrency(prizeFund, item.prizeFund);
    prizeFundFees = addCurrency(prizeFundFees, item.prizeFundFees);
    added = addCurrency(added, item.added);
    expenses = addCurrency(expenses, item.expenses);
    const locked = Boolean(item.locked);
    sources.add(locked ? "Locked" : "Fees");
    if (locked && item.lockedPayouts != null) {
      lockedPayouts = addCurrency(lockedPayouts || 0, item.lockedPayouts);
    }
    const otherFees = mergeOtherFees([item.otherFees]);
    return {
      id: item.id,
      name: item.name || "",
      startDate: item.startDate || "",
      lineage: item.lineage,
      prizeFund: item.prizeFund,
      prizeFundFees: item.prizeFundFees,
      added: item.added,
      expenses: item.expenses,
      otherFees,
      total: addCurrency(item.lineage, item.prizeFund, item.expenses, otherFeeTotal(otherFees)),
      locked,
      lockedPayouts: locked ? item.lockedPayouts : null,
      source: locked ? "Locked" : "Fees",
    };
  });

  const otherFees = mergeOtherFees(tournaments.map((item) => item.otherFees));

  return {
    lineage,
    prizeFund,
    prizeFundFees,
    added,
    expenses,
    otherFees,
    total: addCurrency(lineage, prizeFund, expenses, otherFeeTotal(otherFees)),
    lineageLabel,
    lockedPayouts,
    source: sources.size === 1 ? [...sources][0] : null,
    tournaments,
  };
};

const cents = (amount) => Math.round((Number(amount) || 0) * 100);
const subtractCurrency = (left, right) => (cents(left) - cents(right)) / 100;
const minCurrency = (left, right) => (cents(left) <= cents(right) ? left : right);

const hasBowlerPayments = (moneyIn) =>
  !!moneyIn &&
  ("onlineCollected" in moneyIn ||
    "deskCollected" in moneyIn ||
    "walkUpCollected" in moneyIn ||
    "dueAtDesk" in moneyIn ||
    "refunds" in moneyIn);

/** Online payment + desk + walk-up + still due − refunds. */
const bowlerRevenue = (moneyIn) => {
  if (!hasBowlerPayments(moneyIn)) return null;
  return subtractCurrency(
    addCurrency(
      Number(moneyIn.onlineCollected) || 0,
      Number(moneyIn.deskCollected) || 0,
      Number(moneyIn.walkUpCollected) || 0,
      Number(moneyIn.dueAtDesk) || 0
    ),
    Number(moneyIn.refunds) || 0
  );
};

const bracketPayout = (brackets, moneyIn) => {
  if (moneyIn?.bracketPrize == null && moneyIn?.bracketFees == null) {
    return { prize: 0, fees: 0 };
  }
  const fees = minCurrency(Math.max(0, Number(moneyIn.bracketFees) || 0), brackets);
  const prize = minCurrency(
    Math.max(0, Number(moneyIn.bracketPrize) || 0),
    subtractCurrency(brackets, fees)
  );
  return { prize, fees };
};

/**
 * One tournament's books. Money in and money out use the same dollars.
 * Bowler revenue is online payment + desk + walk-up + still due − refunds.
 * Money added is prize money the director added, which Finance already includes in Prize Fund.
 * Side pots are listed on their own and removed from entry fees so they are not counted twice.
 * Bracket prize fund is 1st plus 2nd. Bracket fees are the house cut.
 * Both are spread across the tree, and they cannot exceed the ticket total.
 */
export const tournamentStatement = (moneyIn, finance) => {
  const sidePots = Number(moneyIn?.sidePots) || 0;
  const brackets = Number(moneyIn?.brackets) || 0;
  const signupEntry = Number(moneyIn?.entryFees) || 0;
  const payout = bracketPayout(brackets, moneyIn);
  const revenue = bowlerRevenue(moneyIn);

  if (!finance) {
    const entryFees =
      revenue == null
        ? signupEntry
        : subtractCurrency(revenue, addCurrency(sidePots, brackets));
    const total = revenue == null ? addCurrency(signupEntry, sidePots, brackets) : revenue;
    return {
      entryFees,
      sidePots,
      brackets,
      added: 0,
      inTotal: total,
      lineage: 0,
      lineageLabel: "Linage",
      prizeFund: 0,
      expenses: 0,
      otherFees: [],
      outEntryFees: entryFees,
      outSidePots: sidePots,
      outBracketPrize: payout.prize,
      outBracketFees: payout.fees,
      outBrackets: brackets,
      outTotal: total,
      hasFeeSplit: false,
    };
  }

  const added = Number(finance.added) || 0;
  const prizeFundFees =
    finance.prizeFundFees != null
      ? Number(finance.prizeFundFees) || 0
      : subtractCurrency(finance.prizeFund, added);
  const feeSum = addCurrency(
    finance.lineage,
    prizeFundFees,
    finance.expenses,
    otherFeeTotal(finance.otherFees)
  );
  const sidePotsFromFees = minCurrency(sidePots, feeSum);
  const sidePotsExtra = subtractCurrency(sidePots, sidePotsFromFees);
  const signupPool = addCurrency(signupEntry, sidePots);
  const uncovered = Math.max(0, subtractCurrency(signupPool, feeSum));
  let entryFees = addCurrency(
    subtractCurrency(feeSum, sidePotsFromFees),
    uncovered
  );
  let inTotal = addCurrency(entryFees, sidePots, brackets, added);
  let outEntryFees = uncovered;
  let outTotal = addCurrency(
    finance.lineage,
    finance.prizeFund,
    finance.expenses,
    otherFeeTotal(finance.otherFees),
    uncovered,
    sidePotsExtra,
    brackets
  );
  if (revenue != null) {
    const target = addCurrency(revenue, added);
    const delta = subtractCurrency(target, inTotal);
    entryFees = subtractCurrency(revenue, addCurrency(sidePots, brackets));
    inTotal = target;
    outEntryFees = addCurrency(outEntryFees, delta);
    outTotal = target;
  }

  return {
    entryFees,
    sidePots,
    brackets,
    added,
    inTotal,
    lineage: Number(finance.lineage) || 0,
    lineageLabel: finance.lineageLabel || "Linage",
    prizeFund: Number(finance.prizeFund) || 0,
    expenses: Number(finance.expenses) || 0,
    otherFees: finance.otherFees || [],
    outEntryFees,
    outSidePots: sidePotsExtra,
    outBracketPrize: payout.prize,
    outBracketFees: payout.fees,
    outBrackets: brackets,
    outTotal,
    hasFeeSplit: true,
  };
};

export const combineStatements = (parts = []) => {
  const list = parts || [];
  if (!list.length) {
    return tournamentStatement(null, null);
  }
  const otherFees = mergeOtherFees(list.map((part) => part.otherFees));
  const lineageLabel = "Linage";
  const summed = {
    entryFees: 0,
    sidePots: 0,
    brackets: 0,
    added: 0,
    inTotal: 0,
    lineage: 0,
    lineageLabel,
    prizeFund: 0,
    expenses: 0,
    otherFees,
    outEntryFees: 0,
    outSidePots: 0,
    outBracketPrize: 0,
    outBracketFees: 0,
    outBrackets: 0,
    outTotal: 0,
    hasFeeSplit: list.some((part) => part.hasFeeSplit),
  };
  list.forEach((part) => {
    summed.entryFees = addCurrency(summed.entryFees, part.entryFees);
    summed.sidePots = addCurrency(summed.sidePots, part.sidePots);
    summed.brackets = addCurrency(summed.brackets, part.brackets);
    summed.added = addCurrency(summed.added, part.added);
    summed.inTotal = addCurrency(summed.inTotal, part.inTotal);
    summed.lineage = addCurrency(summed.lineage, part.lineage);
    summed.prizeFund = addCurrency(summed.prizeFund, part.prizeFund);
    summed.expenses = addCurrency(summed.expenses, part.expenses);
    summed.outEntryFees = addCurrency(summed.outEntryFees, part.outEntryFees);
    summed.outSidePots = addCurrency(summed.outSidePots, part.outSidePots);
    summed.outBracketPrize = addCurrency(
      summed.outBracketPrize,
      part.outBracketPrize
    );
    summed.outBracketFees = addCurrency(
      summed.outBracketFees,
      part.outBracketFees
    );
    summed.outBrackets = addCurrency(summed.outBrackets, part.outBrackets);
    summed.outTotal = addCurrency(summed.outTotal, part.outTotal);
  });
  return summed;
};

const moneyRow = (label, amount) => ({ label, amount: Number(amount) || 0 });

const pushBracketPayout = (rows, books) => {
  if (books.outBracketPrize > 0) {
    rows.push(moneyRow("Bracket prize fund", books.outBracketPrize));
  }
  if (books.outBracketFees > 0) {
    rows.push(moneyRow("Bracket fees", books.outBracketFees));
  }
  const rest = subtractCurrency(
    books.outBrackets,
    addCurrency(books.outBracketPrize, books.outBracketFees)
  );
  if (rest > 0) rows.push(moneyRow("Brackets", rest));
};

/**
 * Rows for the year statement card.
 * Online and Walk-up are signup channels. Card, desk, still due, and refunds
 * are payment rows under Online. Walk-up is the fee total on those teams.
 * Bowler revenue is online payment + desk + walk-up + still due − refunds.
 * Income adds prize money the director added.
 * Allocated uses the statement lines that already equal Income.
 * Bracket prize fund and Bracket fees replace a single Brackets line when the payout split is known.
 */
export const yearStatementSections = ({ rollup, statement, finance }) => {
  const onlineEntries = Number(rollup?.entries) || 0;
  const walkUpEntries = Number(rollup?.walkUpEntries) || 0;
  const books = statement || tournamentStatement(null, null);
  const allocated = [];

  if (books.hasFeeSplit) {
    allocated.push(
      moneyRow(books.lineageLabel || "Linage", books.lineage),
      {
        label: "Prize fund",
        amount: Number(books.prizeFund) || 0,
        lockedPayouts:
          finance?.source === "Locked" && finance.lockedPayouts != null
            ? Number(finance.lockedPayouts) || 0
            : null,
      },
      moneyRow("Expenses", books.expenses)
    );
    (books.otherFees || []).forEach((fee) => {
      if (fee.amount > 0) allocated.push(moneyRow(fee.name, fee.amount));
    });
    if (books.outEntryFees !== 0) {
      allocated.push(moneyRow("Entry fees", books.outEntryFees));
    }
    if (books.outSidePots > 0) {
      allocated.push(moneyRow("Side pots", books.outSidePots));
    }
    pushBracketPayout(allocated, books);
  } else {
    allocated.push(
      moneyRow("Entry fees", books.outEntryFees),
      moneyRow("Side pots", books.outSidePots)
    );
    pushBracketPayout(allocated, books);
  }

  return {
    tournaments: Number(rollup?.tournamentCount) || 0,
    entries: onlineEntries + walkUpEntries,
    onlineEntries,
    walkUpEntries,
    uniqueBowlers: Number(rollup?.uniqueBowlers) || 0,
    channels: [
      {
        label: "Online Sign Ups",
        rows: [
          {
            label: "Online payment",
            count: Number(rollup?.onlinePayments) || 0,
            amount: Number(rollup?.onlineCollected) || 0,
            note: "1",
          },
          {
            label: "Desk",
            count: Number(rollup?.deskPayments) || 0,
            amount: Number(rollup?.deskCollected) || 0,
          },
          {
            label: "Still due",
            count: Number(rollup?.dueCount) || 0,
            amount: Number(rollup?.dueAtDesk) || 0,
          },
          {
            label: "Refunds",
            count: Number(rollup?.refundCount) || 0,
            amount: Number(rollup?.refunds) || 0,
          },
        ],
      },
      {
        label: "Walk-up",
        count: walkUpEntries,
        amount: Number(rollup?.walkUpCollected) || 0,
        rows: [],
      },
    ],
    income: [
      moneyRow("Entry fees", books.entryFees),
      moneyRow("Side pots", books.sidePots),
      moneyRow("Brackets", books.brackets),
      moneyRow("Added to the prize fund", books.added),
    ],
    incomeTotal: books.inTotal,
    allocated,
    allocatedTotal: books.outTotal,
  };
};

const payoutsByTournament = (payouts, eventTournament) => {
  const sums = {};
  (payouts || []).forEach((payout) => {
    const tournamentId = eventTournament.get(payout.eventID);
    if (!tournamentId) return;
    sums[tournamentId] = addCurrency(
      sums[tournamentId] || 0,
      Number(payout.amount) || 0
    );
  });
  return sums;
};

export const loadYearFinance = async ({
  tournaments,
  fetchEventStats,
  fetchPayoutLocks,
  fetchLockedPayouts,
}) => {
  const list = tournaments || [];
  if (!list.length) return null;
  const ids = list.map((tournament) => tournament.id).filter(Boolean);
  const statsList = await Promise.all(ids.map((id) => fetchEventStats(id)));
  const locks = await fetchPayoutLocks(ids);
  const lockedIds = new Set(
    (locks || [])
      .filter((row) => row?.isVerified)
      .map((row) => row.tournamentId)
  );

  const eventTournament = new Map();
  list.forEach((tournament, index) => {
    if (!lockedIds.has(tournament.id)) return;
    (statsList[index] || []).forEach((row) => {
      if (row?.event_id) eventTournament.set(row.event_id, tournament.id);
    });
  });

  const payoutSums = eventTournament.size
    ? payoutsByTournament(
        await fetchLockedPayouts([...eventTournament.keys()]),
        eventTournament
      )
    : {};

  return yearFinance(
    list.map((tournament, index) => {
      const fees = financeFromEventStats(statsList[index]);
      const locked = lockedIds.has(tournament.id);
      return {
        id: tournament.id,
        name: tournament.name,
        startDate: tournament.startDate,
        ...fees,
        locked,
        lockedPayouts: locked ? payoutSums[tournament.id] ?? 0 : null,
      };
    })
  );
};
